import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { TableComponent } from 'components';
import { AddAdminDialog, ConfirmationDialog } from 'components/Dialogs';
import { DEFAULT_TABLE_PARAMS } from 'components/TableComponent/constants';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import PageWithHeader from 'layouts/PageWithHeader/PageWithHeader';
import { adminsModel } from 'models';
import { ROLES, ROUTES } from 'routes/constants';
import { AdminInfo } from 'types';
import { ActionType } from 'types/enums';
import { TableParams } from 'types/other';

function ManageSystemAdmins() {
  const history = useHistory();
  const { t } = useTranslation();
  const { withPageProgressHandler } = useBusyProgressActions();
  const { handleServerError, resetServerErrors } = useServerErrorActions();

  const [admins, setAdmins] = useState<AdminInfo[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [tableParams, setTableParams] = useState<TableParams>({ ...DEFAULT_TABLE_PARAMS });
  const [paginationItemsCount, setPaginationItemsCount] = useState(0);
  const [adminToDelete, setAdminToDelete] = useState<AdminInfo>();

  useEffect(() => {
    getSystemAdmins();
  }, [tableParams]);

  const getSystemAdmins = withPageProgressHandler(() => {
    const { filterId, search, ...rest } = tableParams;

    const names = search ? search.split(' ') : [];
    const firstName = names[0];
    const lastName = names[1] || firstName;

    return adminsModel
      .getAdmins({
        ...rest,
        role: ROLES.systemAdmin,
        ...{ firstName, lastName },
      })
      .then(data => {
        setAdmins(data.admins);
        setPaginationItemsCount(data.adminsCount);
      })
      .catch(handleServerError);
  });

  const handleRedirect = (admin: AdminInfo) => history.push(`${ROUTES.editSystemAdminNamespace}${admin.id}`);

  const handleBlockAdmin = withPageProgressHandler((admin: AdminInfo) =>
    adminsModel.blockAdmin(admin.id, !admin.enabled).then(getSystemAdmins).catch(handleServerError),
  );

  const handleDelete = withPageProgressHandler(({ id }) => {
    setAdminToDelete(undefined);
    return adminsModel.deleteAdmin(id).then(getSystemAdmins).catch(handleServerError);
  });

  const handleDialog = (openValue: boolean) => setIsDialogOpen(openValue);

  const afterActions = () => setTableParams({ ...tableParams, page: 0 });

  const tableData = useMemo(
    () => ({
      headers: [
        t('components.tableComponent.headers.email'),
        t('components.tableComponent.headers.role'),
        t('components.tableComponent.headers.status'),
        t('components.tableComponent.headers.updated'),
      ],
      rows: admins.map(admin => ({
        id: admin.id,
        cells: [admin.email, 'System admin', admin.enabled ? 'Active' : 'Blocked', admin.updatedAt],
        onClick: () => handleRedirect(admin),
        actions: [
          { type: admin.enabled ? ActionType.BLOCK : ActionType.UNBLOCK, onClick: () => handleBlockAdmin(admin) },
          { type: ActionType.EDIT, onClick: () => handleRedirect(admin) },
          { type: ActionType.DELETE, onClick: () => setAdminToDelete(admin) },
        ],
      })),
    }),
    [admins],
  );

  return (
    <PageWithHeader
      tabTitle={t('pages.systemAdmins.title')}
      headerText={`${t('pages.systemAdmins.title')}: ${paginationItemsCount}`}
      button={{
        text: t('pages.systemAdmins.addNewSystemAdmin'),
        onClick: () => handleDialog(true),
      }}
    >
      <TableComponent
        data={tableData}
        count={paginationItemsCount}
        tableParams={tableParams}
        onParamsChange={params => setTableParams(prev => ({ ...prev, ...params }))}
        enableSearch
      />
      <AddAdminDialog
        open={isDialogOpen}
        handleClose={() => handleDialog(false)}
        handleServerError={handleServerError}
        afterActions={afterActions}
        resetServerErrors={resetServerErrors}
        creatingAdminRole={ROLES.systemAdmin}
      />
      {adminToDelete && (
        <ConfirmationDialog
          open
          message={t('pages.systemAdmins.confirmDelete')}
          handleYes={() => handleDelete(adminToDelete)}
          handleNo={() => setAdminToDelete(undefined)}
        />
      )}
    </PageWithHeader>
  );
}

export default ManageSystemAdmins;
